import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Divider,
  Alert,
  Avatar
} from '@mui/material';
import {
  TextFields as TextIcon,
  Image as ImageIcon,
  Splitscreen as DividerIcon,
  Assignment as FormIcon
} from '@mui/icons-material';
import { PageComponent } from '../../types/pages';

// Type guard functions for MUI component variants
const isTypographyVariant = (variant: unknown): variant is 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6' | 'subtitle1' | 'subtitle2' | 'body1' | 'body2' | 'caption' | 'button' | 'overline' => {
  const validVariants = ['h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'subtitle1', 'subtitle2', 'body1', 'body2', 'caption', 'button', 'overline'];
  return typeof variant === 'string' && validVariants.indexOf(variant) !== -1;
};

const isDividerVariant = (variant: unknown): variant is 'fullWidth' | 'inset' | 'middle' => {
  const validVariants = ['fullWidth', 'inset', 'middle'];
  return typeof variant === 'string' && validVariants.indexOf(variant) !== -1;
};

interface ComponentRendererProps {
  component: PageComponent;
}

const ComponentRenderer: React.FC<ComponentRendererProps> = ({ component }) => {
  const renderComponent = () => {
    switch (component.type) {
      case 'text':
        return (
          <Box sx={{ p: 2 }}>
            <Typography
              variant={
                isTypographyVariant(component.props?.variant)
                  ? component.props?.variant
                  : 'body1'
              }
            >
              {String(component.props?.content || 'Text content')}
            </Typography>
          </Box>
        );

      case 'image':
        return (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            {component.props?.src ? (
              <img
                src={component.props.src as string}
                alt={component.props?.alt as string || 'Image'}
                style={{
                  width: component.props?.width as string || '100%',
                  height: component.props?.height as string || 'auto',
                  maxWidth: '100%',
                  borderRadius: '4px'
                }}
              />
            ) : (
              <Box
                sx={{
                  width: component.props?.width as string || '100%',
                  height: component.props?.height as string || '200px',
                  bgcolor: 'grey.100',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  borderRadius: 1,
                  border: '2px dashed',
                  borderColor: 'grey.300'
                }}
              >
                <Box sx={{ textAlign: 'center' }}>
                  <ImageIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
                  <Typography variant="body2" color="text.secondary">
                    {component.props?.alt as string || 'Image placeholder'}
                  </Typography>
                </Box>
              </Box>
            )}
          </Box>
        );

      case 'divider':
        return (
          <Box sx={{ p: 1 }}>
            <Divider 
              variant={isDividerVariant(component.props?.variant) ? component.props?.variant : 'fullWidth'}
              sx={{ 
                borderStyle: component.props?.style as string || 'solid',
                borderWidth: '1px'
              }}
            />
          </Box>
        );

      case 'form':
        return (
          <Paper variant="outlined" sx={{ p: 3, m: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <FormIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h6">
                {component.props?.title as string || 'Form Component'}
              </Typography>
            </Box>
            <Alert severity="info">
              Form ID: {component.props?.formId as string || 'Not specified'}
              <br />
              This is a form component placeholder. In the actual page, the form would be rendered here.
            </Alert>
          </Paper>
        );

      default:
        return (
          <Alert severity="warning" sx={{ m: 1 }}>
            Unknown component type: {component.type}
          </Alert>
        );
    }
  };

  return (
    <Box 
      sx={{ 
        position: 'relative',
        '&:hover': {
          '& .component-overlay': {
            opacity: 1
          }
        }
      }}
    >
      {renderComponent()}
      
      {/* Component type indicator */}
      <Box
        className="component-overlay"
        sx={{
          position: 'absolute',
          top: 4,
          right: 4,
          opacity: 0,
          transition: 'opacity 0.2s',
          zIndex: 1
        }}
      >
        <Avatar
          sx={{
            width: 24,
            height: 24,
            bgcolor: 'primary.main',
            fontSize: '12px'
          }}
        >
          {component.type === 'text' && <TextIcon sx={{ fontSize: 14 }} />}
          {component.type === 'image' && <ImageIcon sx={{ fontSize: 14 }} />}
          {component.type === 'divider' && <DividerIcon sx={{ fontSize: 14 }} />}
          {component.type === 'form' && <FormIcon sx={{ fontSize: 14 }} />}
        </Avatar>
      </Box>
    </Box>
  );
};

export default ComponentRenderer;